<template lang="pug">
span.help-tooltip(
  :class="rootClass",
  @click.stop,
  @mouseenter='showBox',
  @mouseleave='scheduleHide',
  @focusin='showBox',
  @focusout='onFocusOut'
)
  a.help-tooltip__pill(
    v-if='isWarning',
    :href='href',
    target='_blank',
    rel='noopener noreferrer',
    :aria-label='ariaLabel'
  )
    span.help-tooltip__bang !
    span.help-tooltip__pill-text
      slot
      template(v-if='!$slots.default')
        | ヒートマップが
        br
        | 表示されない時
  button.help-tooltip__icon(
    v-else,
    ref='trigger',
    type='button',
    :class="{ 'help-tooltip__icon--bang': isBang }",
    :aria-label='ariaLabel'
  ) {{ triggerMark }}
  .help-tooltip__box(
    v-if='!isWarning',
    ref='box',
    role='tooltip',
    :class='boxClass',
    :style='boxStyle'
  )
    .help-tooltip__body
      span(v-if='resolvedHtml', v-html='resolvedHtml')
      span(v-else-if='text') {{ text }}
</template>

<script>
import { HELP_TOOLTIPS } from '../../constants/popup_help_tooltips'

const GAP = 10
const MARGIN = 8

export default {
  name: 'HelpTooltip',
  props: {
    text: {
      type: String,
      default: ''
    },
    html: {
      type: String,
      default: ''
    },
    htmlKey: {
      type: String,
      default: ''
    },
    ariaLabel: {
      type: String,
      default: '説明'
    },
    variant: {
      type: String,
      default: 'icon'
    },
    href: {
      type: String,
      default: 'https://www.notion.so/n-1/10ba1f34e38c80618ef1c22dac6ebebd?pvs=4#18aa1f34e38c8049ac78e5d4efacfc41'
    }
  },
  data() {
    return {
      open: false,
      placement: 'right',
      boxStyle: {},
      hideTimer: null
    }
  },
  computed: {
    isWarning() {
      return this.variant === 'warning-pill'
    },
    isBang() {
      return this.variant === 'warning'
    },
    triggerMark() {
      return this.isBang ? '!' : '?'
    },
    resolvedHtml() {
      if (this.htmlKey && HELP_TOOLTIPS[this.htmlKey]) {
        return HELP_TOOLTIPS[this.htmlKey]
      }
      return this.html
    },
    rootClass() {
      return {
        'help-tooltip--warning': this.isWarning,
        ['help-tooltip--' + this.placement]: !this.isWarning && this.open
      }
    },
    boxClass() {
      return {
        'is-open': this.open,
        ['help-tooltip__box--' + this.placement]: true
      }
    }
  },
  methods: {
    showBox() {
      if (this.isWarning) {
        return
      }
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
      const alreadyOpen = this.open
      this.open = true
      if (!alreadyOpen) {
        this.boxStyle = { left: '-9999px', top: '0px' }
      }
      this.$nextTick(() => {
        this.reposition()
        this.repositionAfterImages()
        requestAnimationFrame(() => this.reposition())
        if (!alreadyOpen) {
          window.addEventListener('scroll', this.reposition, true)
          window.addEventListener('resize', this.reposition)
        }
      })
    },
    scheduleHide() {
      if (this.isWarning) {
        return
      }
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
      }
      this.hideTimer = setTimeout(() => {
        this.open = false
        this.removeListeners()
      }, 180)
    },
    onFocusOut(event) {
      if (this.$el.contains(event.relatedTarget)) {
        return
      }
      this.scheduleHide()
    },
    removeListeners() {
      window.removeEventListener('scroll', this.reposition, true)
      window.removeEventListener('resize', this.reposition)
    },
    repositionAfterImages() {
      const box = this.$refs.box
      if (!box) {
        return
      }
      box.querySelectorAll('img').forEach((img) => {
        if (img.complete || img.dataset.tooltipSized === '1') {
          return
        }
        img.dataset.tooltipSized = '1'
        const done = () => {
          img.removeEventListener('load', done)
          img.removeEventListener('error', done)
          this.reposition()
        }
        img.addEventListener('load', done)
        img.addEventListener('error', done)
      })
    },
    reposition() {
      const trigger = this.$refs.trigger
      const box = this.$refs.box
      if (!trigger || !box || !this.open) {
        return
      }
      const icon = trigger.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      const bw = box.offsetWidth
      const bh = box.offsetHeight
      const spaceRight = vw - icon.right - GAP - MARGIN
      const spaceLeft = icon.left - GAP - MARGIN
      const spaceAbove = icon.top - GAP - MARGIN
      const spaceBelow = vh - icon.bottom - GAP - MARGIN
      let placement = 'right'
      if (spaceRight >= bw) {
        placement = 'right'
      } else if (spaceLeft >= bw) {
        placement = 'left'
      } else if (spaceAbove >= bh) {
        placement = 'top'
      } else if (spaceLeft > spaceRight) {
        placement = 'left'
      }
      this.placement = placement
      let left
      let top
      if (placement === 'left') {
        left = icon.left - GAP - bw
        top = icon.top + icon.height / 2 - bh / 2
      } else if (placement === 'top') {
        left = icon.left + icon.width / 2 - bw / 2
        top = icon.top - GAP - bh
      } else {
        left = icon.right + GAP
        top = icon.top + icon.height / 2 - bh / 2
      }
      left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - bw - MARGIN))
      top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - bh - MARGIN))
      const edge = 16
      let arrow
      if (placement === 'top') {
        arrow = icon.left + icon.width / 2 - left
        arrow = Math.min(Math.max(arrow, edge), Math.max(edge, bw - edge))
      } else {
        arrow = icon.top + icon.height / 2 - top
        arrow = Math.min(Math.max(arrow, edge), Math.max(edge, bh - edge))
      }
      this.boxStyle = {
        left: left + 'px',
        top: top + 'px',
        '--help-arrow': arrow + 'px'
      }
    }
  },
  beforeDestroy() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
    }
    this.removeListeners()
  }
}
</script>

<style lang="scss" scoped>
$tooltip-border: #dc3545;
$arrow-size: 7px;

.help-tooltip {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  margin-right: 0;
  vertical-align: middle;
  font-weight: 400;

  & + .help-tooltip {
    margin-left: 8px;
  }
}

.help-tooltip__icon {
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #c4c4c4;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover,
  &:focus {
    background: #888;
    outline: none;
  }
}

.help-tooltip__icon--bang {
  background: #e53935;
  font-size: 12px;
  line-height: 16px;

  &:hover,
  &:focus {
    background: #c62828;
  }
}

.help-tooltip__box {
  display: none;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  z-index: 5000;
  width: max-content;
  max-width: 360px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid $tooltip-border;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  color: #333;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
  text-align: left;
  white-space: normal;

  &.is-open {
    display: block;
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-style: solid;
  }

  &::before {
    top: var(--help-arrow, 50%);
    left: -($arrow-size + 1);
    transform: translateY(-50%);
    border-width: $arrow-size $arrow-size $arrow-size 0;
    border-color: transparent $tooltip-border transparent transparent;
  }

  &::after {
    top: var(--help-arrow, 50%);
    left: -$arrow-size;
    transform: translateY(-50%);
    border-width: $arrow-size $arrow-size $arrow-size 0;
    border-color: transparent #fff transparent transparent;
  }
}

.help-tooltip__body {
  max-height: calc(100vh - 48px);
  overflow: auto;
}

.help-tooltip__box--left {
  &::before {
    left: auto;
    right: -($arrow-size + 1);
    border-width: $arrow-size 0 $arrow-size $arrow-size;
    border-color: transparent transparent transparent $tooltip-border;
  }

  &::after {
    left: auto;
    right: -$arrow-size;
    border-width: $arrow-size 0 $arrow-size $arrow-size;
    border-color: transparent transparent transparent #fff;
  }
}

.help-tooltip__box--top {
  &::before {
    top: auto;
    left: var(--help-arrow, 50%);
    bottom: -($arrow-size + 1);
    transform: translateX(-50%);
    border-width: $arrow-size $arrow-size 0 $arrow-size;
    border-color: $tooltip-border transparent transparent transparent;
  }

  &::after {
    top: auto;
    left: var(--help-arrow, 50%);
    bottom: -$arrow-size;
    transform: translateX(-50%);
    border-width: $arrow-size $arrow-size 0 $arrow-size;
    border-color: #fff transparent transparent transparent;
  }
}

.help-tooltip__box ::v-deep .help-tooltip__em,
.help-tooltip__box ::v-deep em {
  color: $tooltip-border;
}

.help-tooltip__box ::v-deep a {
  color: #007bff;
  text-decoration: underline;
}

.help-tooltip__box ::v-deep img {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
  margin-top: 8px;
}

.help-tooltip--warning {
  margin-left: 12px;
  margin-right: 0;
}

.help-tooltip__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px 5px 6px;
  border-radius: 999px;
  background: #e53935;
  color: #fff;
  text-decoration: none;
  line-height: 1.2;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: #c62828;
    color: #fff;
    text-decoration: none;
    outline: none;
  }
}

.help-tooltip__bang {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  color: #e53935;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}

.help-tooltip__pill-text {
  display: block;
  text-align: left;
}
</style>
